/**
 * Guest Info Page
 * Guest information form
 */

import { t } from '../i18n';
import { Header, Card, InputField, TextArea, SelectField, FloatingNav, BackToTopButton, initBackToTop } from '../components';
import { getState, setState, setGuestInfo, navigateTo, setError, loadSavedGuestInfo, saveGuestInfoToStorage } from '../store';
import { getUserProfile } from '../api';
import type { UserGuestProfile } from '../api';
import type { GuestInfo } from '../types';
import { getConfigServices } from '../miniapp-config';

// Cache for loaded profile (to avoid refetching on re-render)
let cachedProfile: UserGuestProfile | null = null;
let profileLoadedForUser: string | null = null;

/**
 * Load user profile from API (call this before rendering)
 */
export async function loadUserProfileAsync(): Promise<void> {
  const state = getState();
  const savedInfo = loadSavedGuestInfo();
  const lookupKey = state.user?.userId || savedInfo?.phone || savedInfo?.email;

  if (!lookupKey) {
    console.log('[GuestInfo] No user identifier, skipping profile load');
    return;
  }

  // Don't refetch if already attempted for this user
  if (profileLoadedForUser === lookupKey && cachedProfile) {
    console.log('[GuestInfo] Using cached profile');
    return;
  }

  try {
    console.log('[GuestInfo] Loading profile from API for:', lookupKey);
    let profile = await getUserProfile(lookupKey);
    if ((!profile || !profile.firstName) && savedInfo?.phone && savedInfo.phone !== lookupKey) {
      profile = await getUserProfile(savedInfo.phone);
    }
    profileLoadedForUser = lookupKey;

    if (profile && (profile.firstName || profile.email || profile.phone)) {
      cachedProfile = profile;
      console.log('[GuestInfo] Profile loaded from API:', profile);
      // Auto-sync into savedGuestInfo if missing
      try {
        const cur = loadSavedGuestInfo() || {};
        saveGuestInfoToStorage({
          ...cur,
          firstName: cur.firstName || profile.firstName || '',
          lastName: cur.lastName || profile.lastName || '',
          email: cur.email || profile.email || '',
          phone: cur.phone || profile.phone || '',
          title: (cur.title || profile.title) as any,
          nationality: cur.nationality || profile.nationality,
          idType: (cur.idType || profile.idType) as any,
          idNumber: cur.idNumber || profile.idNumber,
        } as any);
      } catch (_) {}
    } else {
      cachedProfile = null;
      console.log('[GuestInfo] No profile found in API');
    }
  } catch (error) {
    console.error('[GuestInfo] Failed to load profile from API:', error);
    profileLoadedForUser = lookupKey;
    cachedProfile = null;
  }
}

/**
 * Render guest info page
 */
export function renderGuestInfoPage(): string {
  const state = getState();
  const { selectedRoom, guestInfo, user } = state;

  if (!selectedRoom) {
    navigateTo('search');
    return '';
  }

  // Check if we're in edit mode
  const editBookingData = sessionStorage.getItem('editBookingData');
  const isEditMode = !!editBookingData;
  let originalBooking: any = null;

  if (isEditMode && editBookingData) {
    try {
      originalBooking = JSON.parse(editBookingData);
      console.log('[GuestInfo] Edit mode detected, original booking:', originalBooking);
    } catch (error) {
      console.error('[GuestInfo] Failed to parse editBookingData:', error);
    }
  }

  // Load saved guest info from localStorage (fallback)
  const savedInfo = loadSavedGuestInfo();

  // Use cached API profile if available
  const apiProfile = cachedProfile;

  console.log('[GuestInfo] Rendering with:');
  console.log('[GuestInfo] - guestInfo (session):', guestInfo);
  console.log('[GuestInfo] - apiProfile (Firestore):', apiProfile);
  console.log('[GuestInfo] - savedInfo (localStorage):', savedInfo);
  console.log('[GuestInfo] - originalBooking (edit mode):', originalBooking);
  console.log('[GuestInfo] - user (LINE profile):', user);

  // Priority: originalBooking (if editing) > guestInfo (current session) > savedInfo (localStorage) > apiProfile (Firestore) > user (LINE profile)
  const defaultFirstName =
    originalBooking?.guestFirstName ||
    guestInfo?.firstName ||
    savedInfo?.firstName ||
    apiProfile?.firstName ||
    (user?.displayName && user.displayName !== 'Guest' ? user.displayName.split(' ')[0] : '') ||
    '';
  const defaultLastName =
    originalBooking?.guestLastName ||
    guestInfo?.lastName ||
    savedInfo?.lastName ||
    apiProfile?.lastName ||
    (user?.displayName && user.displayName !== 'Guest' ? user.displayName.split(' ').slice(1).join(' ') : '') ||
    '';
  const defaultEmail =
    originalBooking?.guestEmail || guestInfo?.email || apiProfile?.email || savedInfo?.email || '';
  const defaultPhone =
    originalBooking?.guestPhone || guestInfo?.phone || apiProfile?.phone || savedInfo?.phone || '';
  const defaultSpecialRequests =
    originalBooking?.specialRequests || guestInfo?.specialRequests || '';
  const defaultTitle = (
    guestInfo?.title || apiProfile?.title || savedInfo?.title || ''
  ) as string;
  const defaultNationality =
    guestInfo?.nationality || apiProfile?.nationality || savedInfo?.nationality || '';
  const defaultIdType = (
    guestInfo?.idType || apiProfile?.idType || savedInfo?.idType || ''
  ) as string;
  const defaultIdNumber =
    guestInfo?.idNumber || apiProfile?.idNumber || savedInfo?.idNumber || '';

  console.log('[GuestInfo] Default values:', {
    defaultFirstName,
    defaultLastName,
    defaultEmail,
    defaultPhone,
    defaultSpecialRequests,
  });

  const isSpa = state.bookingType === 'SPA';
  const isRestaurant = state.bookingType === 'RESTAURANT';
  const isMice = state.bookingType === 'MICE';
  const isAppointment = state.bookingType === 'APPOINTMENT' || state.engine === 'appointment';
  const isRfp = state.bookingType === 'RFP' || state.engine === 'request-rfp' || state.engine === 'request_rfp';

  const pageTitle = isAppointment
    ? t('registrantInfoTitle')
    : isRfp
      ? t('requesterInfoTitle')
      : isMice
        ? (state.language === 'th' ? 'ข้อมูลผู้ติดต่อ / ผู้จัดงาน' : state.language === 'zh' ? '联系人/组织者信息' : 'Organizer & Contact Info')
        : isSpa
          ? t('spaGuestInfoTitle')
          : isRestaurant
            ? t('diningGuestInfoTitle')
            : t('guestInfoTitle');

  return `
    ${Header(pageTitle, false)}
    
    <div class="page-content" id="guest-content">
      <form id="guest-form" class="guest-form">
        ${Card(`
          ${SelectField(t('guestTitle'), 'title', [
            { value: '', text: t('selectTitle') },
            { value: 'mr', text: t('titleMr') },
            { value: 'mrs', text: t('titleMrs') },
            { value: 'ms', text: t('titleMs') },
            { value: 'miss', text: t('titleMiss') },
            { value: 'dr', text: t('titleDr') },
          ], defaultTitle, false)}

          <div class="form-row">
            <div class="form-col">
              ${InputField(t('firstName'), 'firstName', 'text', defaultFirstName, true)}
            </div>
            <div class="form-col">
              ${InputField(t('lastName'), 'lastName', 'text', defaultLastName, true)}
            </div>
          </div>
          
          ${InputField(t('nationality'), 'nationality', 'text', defaultNationality, false, t('nationalityPlaceholder'))}

          ${SelectField(t('idType'), 'idType', [
            { value: '', text: t('selectIdType') },
            { value: 'passport', text: t('idTypePassport') },
            { value: 'national_id', text: t('idTypeNationalId') },
            { value: 'driving_license', text: t('idTypeDrivingLicense') },
          ], defaultIdType, false)}

          <div id="idNumber-wrapper" style="${defaultIdType ? '' : 'display: none;'}">
            ${InputField(t('idNumber'), 'idNumber', 'text', defaultIdNumber, false, t('idNumberPlaceholder'))}
          </div>

          ${InputField(t('email'), 'email', 'email', defaultEmail, true, 'example@email.com')}
          
          ${InputField(t('phone'), 'phone', 'tel', defaultPhone, true, '+66 XX XXX XXXX')}

          ${(() => {
            const currentServiceId = state.activeServiceId;
            if (!currentServiceId) return '';
            const services = getConfigServices();
            const activeSvc = services.find((s: any) => s.id === currentServiceId);
            if (!activeSvc || !Array.isArray(activeSvc.fields) || activeSvc.fields.length === 0) return '';
            const lang = (state.language || 'th');
            return `
              <div style="margin-top: 16px; padding-top: 16px; border-top: 1px dashed #e2e8f0;">
                <div style="font-size: 13px; font-weight: 700; color: #475569; margin-bottom: 10px;">📋 ข้อมูลเพิ่มเติม (Additional Information)</div>
                ${activeSvc.fields.map((f: any) => {
                  const fLabel = typeof f.label === 'object' ? (f.label[lang] || f.label.en || f.label.th) : (f.label || f.key);
                  const fVal = (state.customFields && state.customFields[f.key]) || '';
                  if (f.type === 'textarea') {
                    return TextArea(fLabel, `custom_${f.key}`, fVal, fLabel);
                  }
                  if (f.type === 'select' && Array.isArray(f.options)) {
                    const opts = [
                      { value: '', text: `-- ${fLabel} --` },
                      ...f.options.map((o: any) => ({
                        value: o.value,
                        text: typeof o.label === 'object' ? (o.label[lang] || o.label.en || o.label.th) : o.label,
                      })),
                    ];
                    return SelectField(fLabel, `custom_${f.key}`, opts, fVal, f.required);
                  }
                  return InputField(fLabel, `custom_${f.key}`, f.type === 'number' ? 'number' : 'text', fVal, f.required, fLabel);
                }).join('')}
              </div>
            `;
          })()}

          ${TextArea(t('specialRequests'), 'specialRequests', defaultSpecialRequests, t('specialRequests'))}

          <input type="hidden" id="roomNumber" name="roomNumber" value="${(guestInfo as any)?.roomNumber || originalBooking?.roomNumber || ''}" />
        `)}
        
        <div class="form-actions">
          <button type="submit" class="btn btn-primary btn-block btn-lg" id="submit-btn" style="display:none;">
            ${t('next')}
          </button>
        </div>
      </form>
    </div>

    ${BackToTopButton('guest')}

    ${FloatingNav()}
  `;
}

/**
 * Initialize guest info page event handlers
 */
export function initGuestInfoPage(): void {
  const form = document.getElementById('guest-form');
  const phoneInput = document.getElementById('phone') as HTMLInputElement;
  const phoneError = document.getElementById('phone-error');
  const emailInput = document.getElementById('email') as HTMLInputElement;
  const emailError = document.getElementById('email-error');

  // Show/hide idNumber field based on idType selection
  const idTypeSelect = document.getElementById('idType') as HTMLSelectElement | null;
  const idNumberWrapper = document.getElementById('idNumber-wrapper');
  idTypeSelect?.addEventListener('change', () => {
    if (idNumberWrapper) {
      idNumberWrapper.style.display = idTypeSelect.value ? '' : 'none';
    }
  });

  // Back button handler
  window.handleBack = () => {
    const currentState = getState();
    if (currentState.bookingType === 'SPA') {
      navigateTo('spa');
    } else if (currentState.bookingType === 'RESTAURANT') {
      navigateTo('restaurant');
    } else if (currentState.bookingType === 'APPOINTMENT' || currentState.engine === 'appointment' || currentState.bookingType === 'RFP' || currentState.engine === 'request-rfp' || currentState.engine === 'request_rfp') {
      navigateTo('service');
    } else if (currentState.bookingType === 'MICE') {
      navigateTo('service');
    } else {
      navigateTo('rooms');
    }
  };

  // Form validation
  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // Remove all non-digits and check length
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 9 && digits.length <= 15;
  };

  // Real-time phone validation on blur
  // Real-time phone validation and smart profile lookup on blur
  phoneInput?.addEventListener('blur', async () => {
    const phone = phoneInput.value.trim();
    if (phone && !validatePhone(phone)) {
      phoneInput.classList.add('input-error');
      if (phoneError) phoneError.textContent = t('invalidPhone');
    } else {
      phoneInput.classList.remove('input-error');
      if (phoneError) phoneError.textContent = '';

      if (phone && validatePhone(phone)) {
        const curFirst = (document.getElementById('firstName') as HTMLInputElement)?.value.trim();
        const curLast = (document.getElementById('lastName') as HTMLInputElement)?.value.trim();
        const curEmail = (document.getElementById('email') as HTMLInputElement)?.value.trim();

        // If any key field is missing, auto-fetch profile from database
        if (!curFirst || !curLast || !curEmail) {
          try {
            const profile = await getUserProfile(phone);
            if (profile && (profile.firstName || profile.email)) {
              if (!curFirst && profile.firstName) {
                const el = document.getElementById('firstName') as HTMLInputElement | null;
                if (el) el.value = profile.firstName;
              }
              if (!curLast && profile.lastName) {
                const el = document.getElementById('lastName') as HTMLInputElement | null;
                if (el) el.value = profile.lastName;
              }
              if (!curEmail && profile.email) {
                const el = document.getElementById('email') as HTMLInputElement | null;
                if (el) el.value = profile.email;
              }
              if (profile.title) {
                const el = document.getElementById('title') as HTMLSelectElement | null;
                if (el && !el.value) el.value = profile.title;
              }
              if (profile.nationality) {
                const el = document.getElementById('nationality') as HTMLInputElement | null;
                if (el && !el.value) el.value = profile.nationality;
              }
              if (profile.idType) {
                const el = document.getElementById('idType') as HTMLSelectElement | null;
                if (el && !el.value) el.value = profile.idType;
              }
              if (profile.idNumber) {
                const el = document.getElementById('idNumber') as HTMLInputElement | null;
                if (el && !el.value) el.value = profile.idNumber;
              }
            }
          } catch (_) {}
        }
      }
    }
    updateForwardState();
  });

  // Clear error on input and update forward button state
  phoneInput?.addEventListener('input', () => {
    phoneInput.classList.remove('input-error');
    if (phoneError) phoneError.textContent = '';
    updateForwardState();
  });

  // Update the footer forward button enabled/disabled state based on validation
  function updateForwardState() {
    try {
      const forwardBtn = document.querySelector('.floating-forward') as HTMLButtonElement | null;
      if (!forwardBtn) return;

      const phone = phoneInput?.value.trim() || '';
      // Consider empty phone as invalid (user must enter)
      const valid = validatePhone(phone);

      forwardBtn.disabled = !valid;
      if (!valid) {
        forwardBtn.setAttribute('aria-disabled', 'true');
        forwardBtn.classList.add('disabled');
      } else {
        forwardBtn.removeAttribute('aria-disabled');
        forwardBtn.classList.remove('disabled');
      }
    } catch (e) {
      // ignore
    }
  }

  // Initial forward state
  updateForwardState();

  // Real-time email validation on blur
  emailInput?.addEventListener('blur', () => {
    const email = emailInput.value.trim();
    if (email && !validateEmail(email)) {
      emailInput.classList.add('input-error');
      if (emailError) emailError.textContent = t('invalidEmail');
    } else {
      emailInput.classList.remove('input-error');
      if (emailError) emailError.textContent = '';
    }
  });

  // Clear error on input
  emailInput?.addEventListener('input', () => {
    emailInput.classList.remove('input-error');
    if (emailError) emailError.textContent = '';
  });

  // Form submission
  form?.addEventListener('submit', async e => {
    e.preventDefault();

    const formData = new FormData(form as HTMLFormElement);
    const titleVal = (formData.get('title') as string || '').trim();
    const nationalityVal = (formData.get('nationality') as string || '').trim();
    const idTypeVal = (formData.get('idType') as string || '').trim();
    const idNumberVal = (formData.get('idNumber') as string || '').trim();
    const guestInfo: GuestInfo = {
      firstName: (formData.get('firstName') as string).trim(),
      lastName: (formData.get('lastName') as string).trim(),
      email: (formData.get('email') as string).trim(),
      phone: (formData.get('phone') as string).trim(),
      specialRequests: (formData.get('specialRequests') as string).trim() || undefined,
      title: titleVal ? (titleVal as GuestInfo['title']) : undefined,
      nationality: nationalityVal || undefined,
      idType: idTypeVal ? (idTypeVal as GuestInfo['idType']) : undefined,
      idNumber: idNumberVal || undefined,
    };

    // Validate
    if (!guestInfo.firstName || !guestInfo.lastName) {
      setError(t('required'));
      return;
    }

    if (!validateEmail(guestInfo.email)) {
      setError(t('invalidEmail'));
      return;
    }

    if (!validatePhone(guestInfo.phone)) {
      setError(t('invalidPhone'));
      return;
    }

    // Persist guest info for future auto-fill
    try {
      saveGuestInfoToStorage(guestInfo);
    } catch (_) {}

    // Extract custom intake fields
    const customFields: Record<string, any> = { ...(getState().customFields || {}) };
    for (const [k, v] of formData.entries()) {
      if (k.startsWith('custom_')) {
        const fieldKey = k.replace('custom_', '');
        customFields[fieldKey] = typeof v === 'string' ? v.trim() : v;
      }
    }

    setState({ guestInfo, customFields, currentPage: 'confirm', error: null });
  });

  // Expose handleForward to allow footer forward button to submit form
  window.handleForward = () => {
    const form = document.getElementById('guest-form') as HTMLFormElement | null;
    if (form) {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }
  };

  // Floating "BACK TO TOP" for Guest page
  initBackToTop('guest');
}

// Extend window for global handlers
declare global {
  interface Window {
    handleBack: () => void;
    handleForward?: () => void;
  }
}
